'use client';

import React, { PropsWithChildren, useCallback, useMemo, useState, useTransition } from 'react';
import Select from '@/src/app/components/Select/Select';
import { usePathname, useRouter } from 'next/navigation';
import { Locale } from '@/src/lib/store/types';
import { useFormHelpers } from '@/src/lib/utils/form-helpers';
import { isKnownLanguageInPathname, isSystemUrl, transformURLForFrontendPaths } from '@/src/lib/utils/url';
import { Dictionary } from '@/src/lib/dictionary/types';
import { transformExceptionToErrorObject } from '@/src/lib/utils/error';
import { setClientLocaleCookie } from '@/src/lib/dictionary/utils';
import { getLocalizedPathnameForNewLanguage } from '@/src/lib/url-resolver/business/get-localized-pathname.actions';
import { isGlueErrorResponse } from '@/src/lib/glue/business/is-glue-error';
import { handleException } from '@/src/lib/core/exception/handle-exception';
import { isFailure } from '@/src/lib/http/business/server-action-results';
import { isAllProductsUrl } from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/lib/utils/category.utils';
import { IconName } from '@/src/app/components/Icon/types';
export interface LanguageDropdownProps {
  locales: Array<{
    code: string;
    name: Locale;
  }>;
  dict: Dictionary;
  lang: string;
  country: string;
  getDictionary: (lang: string) => Promise<Dictionary>;
  storeId: string;
  alignOptionsTop?: boolean;
}
function LanguageDropdown({
  locales,
  dict,
  country,
  lang,
  getDictionary,
  storeId,
  alignOptionsTop
}: PropsWithChildren<LanguageDropdownProps>) {
  const [loading, setLoading] = useState(false);
  const {
    handleErrors
  } = useFormHelpers(dict);
  const router = useRouter();
  const pathname = usePathname() || '';
  const [isPending, startTransition] = useTransition();
  const options = useMemo(() => {
    return locales.map(locale => ({
      value: locale.code,
      label: `${storeId in dict.stores ? dict.stores[storeId as keyof typeof dict.stores] : ''} | ${
      // @ts-expect-error - TS doesn't know that dict.locales[locale.name] exists
      dict.locales[locale.name]}`
    }));
  }, [locales, storeId, dict]);
  const onSelectionChangeHook = useCallback(async ({
    value
  }: {
    value: string;
  }) => {
    const newLanguage = value as string;
    if (lang === newLanguage) return;
    const newPathname = `/${newLanguage}`;
    let currentSearchParams = new URLSearchParams(window.location.search).toString();
    currentSearchParams = currentSearchParams.length ? `?${currentSearchParams}` : '';
    if (!isKnownLanguageInPathname(pathname, country)) {
      startTransition(() => router.push(transformURLForFrontendPaths(`${newPathname}${currentSearchParams}`, newLanguage, country)));
      return;
    }
    setClientLocaleCookie(newLanguage);

    // "all products" is not a real category and needs to be translated and redirected client-side
    if (isAllProductsUrl(pathname.split('/'), dict)) {
      const newLangDict = await getDictionary(newLanguage);
      startTransition(() => router.push(transformURLForFrontendPaths(`/${newLangDict.category.allProductsSlug}${currentSearchParams}`, newLanguage, country)));
      return;
    }
    if (isSystemUrl(pathname, lang, country)) {
      startTransition(() => router.push(`${pathname?.replace(`/${lang}`, `/${newLanguage}`)}${currentSearchParams}`));
      return;
    }
    try {
      setLoading(true);
      const newPathnameResponse = await getLocalizedPathnameForNewLanguage(pathname, lang, newLanguage, country);
      if (isFailure(newPathnameResponse)) {
        throw newPathnameResponse.error;
      }
      startTransition(async () => {
        router.push(transformURLForFrontendPaths(`${newPathnameResponse.data}${currentSearchParams}`, newLanguage, country));
      });
    } catch (error) {
      const err = transformExceptionToErrorObject(error);
      if (isGlueErrorResponse(err) && err.errors[0].status !== 404) {
        handleErrors(err);
        return;
      }
      handleException(err);
    } finally {
      setLoading(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dict.category.allProductsSlug, getDictionary, lang, pathname, router]);
  return <Select options={options} label={dict.general.storeName} value={lang} placeholder={dict.general.selectFieldPlaceholder} onSelectionChangeHook={onSelectionChangeHook} name="language-toggle" className="min-w-[16rem]" labelIcon={IconName.PUBLIC} disabled={loading || isPending} testId="language-toggle" alignOptionsTop={alignOptionsTop} />;
}
export default LanguageDropdown;